/** Fields, seats and lanes in document mode (E3, spec 2026-09-18 §6 Edit):
 * an author adds and removes them and edits what Customize locks — a
 * field's type and options. Removing one a step still names is not refused
 * here: the validate bar reports every dangling reference by path. */
import { useState, type ReactNode } from 'react'
import { Button, Input, NativeSelect, Switch, TagInput } from '@/components/ui'
import type { ProcessFieldJson, ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { KEY_PATTERN } from './stepSkeleton'
import { ProcessEditorCard } from './ProcessEditorCard'
import { FIELDS_CARD, LANES_CARD, SEATS_CARD, type CardId, type EditorCards } from './editorCards'
import { fieldsSummary, lanesSummary, seatsSummary } from './editorSummaries'

type SeatJson = ProcessWorkflowDefinitionJson['seats'][string]
type LaneJson = ProcessWorkflowDefinitionJson['lanes'][string]

/** Mirrors `ProcessFieldType` minus `table` in backend/app/schemas/process_workflow.py. */
const FIELD_TYPES = ['string', 'text', 'integer', 'number', 'boolean', 'date', 'datetime', 'time', 'enum', 'phone', 'email', 'place', 'window']

/** A localized text with one language cleared loses that key, not the map. */
function withText(map: Record<string, string> | null | undefined, lang: string, text: string) {
  const next = { ...(map ?? {}) }
  if (text === '') delete next[lang]
  else next[lang] = text
  return next
}

/** SA-1060: each panel is a closed card with a summary line (spec 2026-10-09 §2.7). */
function Panel({
  cardId, title, hint, summary, cards, edited, children,
}: {
  cardId: CardId; title: string; hint: string; summary: string; cards: EditorCards; edited: boolean; children: ReactNode
}) {
  return (
    <ProcessEditorCard
      cardId={cardId}
      title={title}
      label={title}
      summary={summary}
      edited={edited}
      open={cards.isOpen(cardId)}
      onOpenChange={(open) => cards.setOpen(cardId, open)}
    >
      <p className="text-xs text-muted-foreground">{hint}</p>
      {children}
    </ProcessEditorCard>
  )
}

function AddRow({ noun, taken, onAdd }: { noun: string; taken: string[]; onAdd: (id: string) => void }) {
  const [id, setId] = useState('')
  const ok = KEY_PATTERN.test(id) && !taken.includes(id)
  const add = () => {
    if (!ok) return
    onAdd(id)
    setId('')
  }
  return (
    <div className="flex items-center gap-2 border-t border-border/60 pt-3">
      <Input
        aria-label={`New ${noun} id`}
        className="max-w-48"
        placeholder={`new_${noun}_id`}
        value={id}
        onChange={(e) => setId(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') add()
        }}
      />
      <Button size="sm" variant="outline" disabled={!ok} onClick={add}>
        Add {noun}
      </Button>
    </div>
  )
}

export function ProcessDocumentPanels({
  doc,
  roles,
  onField,
  onSeat,
  onLane,
  cards,
  edited,
}: {
  doc: ProcessWorkflowDefinitionJson
  /** Role names to suggest on a seat; may be empty (role names are data). */
  roles: string[]
  onField: (id: string, spec: ProcessFieldJson | null) => void
  onSeat: (id: string, spec: SeatJson | null) => void
  onLane: (id: string, spec: LaneJson | null) => void
  /** SA-1060: the page's open state for every card. */
  cards: EditorCards
  /** SA-1060: the cards the validate diff names (ruling Q3). */
  edited: Set<CardId>
}) {
  const languages = doc.languages?.length ? doc.languages : ['en']
  const suffix = (l: string) => (l === languages[0] ? '' : ` (${l})`)
  const title = (id: string) => id.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

  return (
    <>
      <Panel cardId={FIELDS_CARD} title="Fields" hint="What the steps read and write. Steps name them in needs, produces and requires." summary={fieldsSummary(doc)} cards={cards} edited={edited.has(FIELDS_CARD)}>
        {Object.entries(doc.fields ?? {}).map(([id, field]) => (
          <div key={id} className="space-y-2 border-t border-border/60 pt-3 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-muted-foreground">{id}</span>
              <Button size="sm" variant="ghost" aria-label={`Remove field ${id}`} onClick={() => onField(id, null)}>
                Remove
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1 font-medium text-foreground">
                <span>Type</span>
                <NativeSelect
                  aria-label={`Type of ${id}`}
                  disabled={field.type === 'table'}
                  value={field.type}
                  onChange={(e) => {
                    // Options belong to an enum and `alternatives` to a place: a re-typed field keeps neither (the strict model refuses both).
                    const { options: _drop, alternatives: _alt, ...rest } = field
                    // Only an enum carries options; a fresh enum needs one to be valid.
                    onField(id, e.target.value === 'enum' ? { ...rest, type: 'enum', options: field.options ?? [] } : { ...rest, type: e.target.value })
                  }}
                >
                  {!FIELD_TYPES.includes(field.type) && <option value={field.type}>{field.type}</option>}
                  {FIELD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </NativeSelect>
                {/* SA-449: a table's columns are contract, authored in the Raw view; switching
                    the type here would strand them (the strict model refuses `columns` on a scalar). */}
                {field.type === 'table' && <span className="block text-muted-foreground">Edit a table field in the Raw view.</span>}
              </label>
              <div className="flex items-center gap-2 font-medium text-foreground sm:pt-5">
                <Switch
                  size="sm"
                  aria-label={`${id} is optional`}
                  checked={field.optional ?? false}
                  onCheckedChange={(optional) => onField(id, { ...field, optional })}
                />
                Optional
              </div>
              {languages.map((l) => (
                <label key={`label:${l}`} className="block space-y-1 font-medium text-foreground">
                  <span>Label{suffix(l)}</span>
                  <Input
                    aria-label={`Label of ${id}${suffix(l)}`}
                    value={field.label?.[l] ?? ''}
                    onChange={(e) => onField(id, { ...field, label: withText(field.label, l, e.target.value) })}
                  />
                </label>
              ))}
              {languages.map((l) => (
                <label key={`help:${l}`} className="block space-y-1 font-medium text-foreground">
                  <span>Help{suffix(l)}</span>
                  <Input
                    aria-label={`Help of ${id}${suffix(l)}`}
                    value={field.help?.[l] ?? ''}
                    onChange={(e) => {
                      const help = withText(field.help, l, e.target.value)
                      const { help: _drop, ...rest } = field
                      onField(id, Object.keys(help).length > 0 ? { ...field, help } : rest)
                    }}
                  />
                </label>
              ))}
            </div>
            {field.type === 'enum' && (
              <div className="space-y-1">
                <span className="font-medium text-foreground">Options</span>
                <TagInput
                  ariaLabel={`Options of ${id}`}
                  value={field.options ?? []}
                  onChange={(options) => onField(id, { ...field, options })}
                  normalize={(raw) => raw.trim()}
                  maxTags={200}
                  placeholder="Add an option and press Enter"
                />
              </div>
            )}
          </div>
        ))}
        <AddRow
          noun="field"
          taken={Object.keys(doc.fields ?? {})}
          onAdd={(id) => onField(id, { type: 'string', label: { [languages[0]]: title(id) } })}
        />
      </Panel>

      <Panel cardId={SEATS_CARD} title="Seats" hint="Who may work a seat's tasks. A seat with no roles is open to everyone who can work tasks." summary={seatsSummary(doc, {})} cards={cards} edited={edited.has(SEATS_CARD)}>
        {Object.entries(doc.seats ?? {}).map(([id, seat]) => (
          <div key={id} className="space-y-2 border-t border-border/60 pt-3 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-muted-foreground">{id}</span>
              <Button size="sm" variant="ghost" aria-label={`Remove seat ${id}`} onClick={() => onSeat(id, null)}>
                Remove
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                {languages.map((l) => (
                  <label key={l} className="block space-y-1 font-medium text-foreground">
                    <span>Label{suffix(l)}</span>
                    <Input
                      aria-label={`Label of seat ${id}${suffix(l)}`}
                      value={seat.label?.[l] ?? ''}
                      onChange={(e) => onSeat(id, { ...seat, label: withText(seat.label, l, e.target.value) })}
                    />
                  </label>
                ))}
              </div>
              <div className="space-y-1">
                <span className="font-medium text-foreground">Roles</span>
                <TagInput
                  ariaLabel={`Roles of seat ${id}`}
                  value={seat.roles ?? []}
                  onChange={(next) => onSeat(id, { ...seat, roles: next })}
                  suggestions={roles}
                  normalize={(raw) => raw.trim()}
                  placeholder="Add a role and press Enter"
                />
              </div>
            </div>
          </div>
        ))}
        <AddRow
          noun="seat"
          taken={Object.keys(doc.seats ?? {})}
          onAdd={(id) => onSeat(id, { label: { [languages[0]]: title(id) }, roles: [] })}
        />
      </Panel>

      <Panel cardId={LANES_CARD} title="Lanes" hint="The rows of the map. Every step sits in one." summary={lanesSummary(doc)} cards={cards} edited={edited.has(LANES_CARD)}>
        {Object.entries(doc.lanes ?? {}).map(([id, lane]) => (
          <div key={id} className="grid gap-3 border-t border-border/60 pt-3 text-xs sm:grid-cols-[10rem_1fr_auto] sm:items-end">
            <span className="font-mono text-muted-foreground">{id}</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {languages.map((l) => (
                <label key={l} className="block space-y-1 font-medium text-foreground">
                  <span>Label{suffix(l)}</span>
                  <Input
                    aria-label={`Label of lane ${id}${suffix(l)}`}
                    value={lane.label?.[l] ?? ''}
                    onChange={(e) => onLane(id, { ...lane, label: withText(lane.label, l, e.target.value) })}
                  />
                </label>
              ))}
            </div>
            <Button size="sm" variant="ghost" aria-label={`Remove lane ${id}`} onClick={() => onLane(id, null)}>
              Remove
            </Button>
          </div>
        ))}
        <AddRow
          noun="lane"
          taken={Object.keys(doc.lanes ?? {})}
          onAdd={(id) => onLane(id, { label: { [languages[0]]: title(id) } })}
        />
      </Panel>
    </>
  )
}
